/**
 * What the phone recap video shows, as plain data. Pure — unit-tested in
 * recap-scene.test.js. The canvas drawing lives in recap/compositor.js.
 *
 * Nothing here chooses a winner: it only lays out results the draw engine has
 * already committed (see "Invariants" in CLAUDE.md).
 */
import { creatorSubtitle, creatorTitle } from './creator.js';
import { winnerKeys } from './entries.js';

// A recording only needs the names that scroll past in ~40 s, not thousands of rows.
export const INTRO_ROW_LIMIT = 200;

/** The intro page: who hosts the giveaway and who is taking part. */
export function introScene(state, entries) {
  const won = winnerKeys(state.winners);
  return {
    title: creatorTitle(state.creator),
    subtitle: creatorSubtitle(state.creator),
    total: entries.length,
    rows: entries.slice(0, INTRO_ROW_LIMIT).map((entry) => ({
      name: entry.displayName || entry.username,
      comment: entry.comment,
      avatar: entry.avatar,
      won: won.has(entry.key),
    })),
  };
}

/** The results page: valid winners grouped by tier in tier order (voided ones left out). */
export function winnersScene(state) {
  const active = state.winners.filter((w) => !w.voided);
  const items = [];
  for (const tier of state.tiers) {
    const winners = active.filter((w) => w.tierId === tier.id);
    if (!winners.length) continue;
    items.push({ kind: 'group', title: tier.name || 'Untitled tier', prize: tier.prize || '' });
    winners.forEach((w, i) =>
      items.push({
        kind: 'winner',
        rank: i + 1,
        name: w.displayName || w.username,
        handle: `@${w.username}`,
        comment: w.comment,
      }),
    );
  }
  return { title: creatorTitle(state.creator), total: active.length, items };
}

/**
 * The slot reel rows to draw around a fractional centre row.
 * `offset` is in rows from the centre (0 = centred, ±1 = the rows above/below).
 */
export function visibleSlotRows(rows, centre, span = 2) {
  const from = Math.max(0, Math.floor(centre) - span);
  const to = Math.min(rows.length - 1, Math.ceil(centre) + span);
  const out = [];
  for (let index = from; index <= to; index++) out.push({ row: rows[index], offset: index - centre });
  return out;
}

const SCROLL_LOOP_SECONDS = 40; // aim to pass the whole list in this time…
const SCROLL_MIN_SPEED = 16; // …but never crawl (px/s)…
const SCROLL_MAX_SPEED = 60; // …or rush past names faster than people can read them
const SCROLL_EDGE_PAUSE_MS = 2500;

/**
 * Auto-scroll position `elapsedMs` after a list appears: pause, glide to the
 * bottom, pause, then start over. Same pacing as ui/autoscroll.js.
 */
export function scrollOffset(elapsedMs, contentHeight, viewHeight) {
  const max = contentHeight - viewHeight;
  if (max <= 1) return 0;
  const speed = Math.min(SCROLL_MAX_SPEED, Math.max(SCROLL_MIN_SPEED, max / SCROLL_LOOP_SECONDS));
  const travelMs = (max / speed) * 1000;
  const t = elapsedMs % (SCROLL_EDGE_PAUSE_MS * 2 + travelMs);
  if (t < SCROLL_EDGE_PAUSE_MS) return 0;
  if (t > SCROLL_EDGE_PAUSE_MS + travelMs) return max;
  return ((t - SCROLL_EDGE_PAUSE_MS) / 1000) * speed;
}

/** Start/end angles (radians, from 12 o'clock) of the countdown ring for a remaining fraction. */
export function ringSweep(fraction) {
  const start = -Math.PI / 2;
  const clamped = Math.min(1, Math.max(0, fraction));
  return { start, end: start + clamped * Math.PI * 2 };
}
